import { POPULAR_LOCALES } from 'app/core/constants'

const DEFAULT_HINT = 'You can manage info for the selected language.'

/**
 * LocalizedMetadataModalCtrl
 * Modal controller for adding, editing, or viewing a single localization
 * entry on any localizable entity (content metadata, modules, shelves,
 * category rows, content sets, partner products, or read-only shelf-as-row).
 */
/* @ngInject */
export default function LocalizedMetadataModalCtrl (
  $scope,
  $uibModalInstance,
  Localization,
  alertLiteSvc,
  entity,
  config,
  isEdit,
  viewOnly,
  nameLabel,
  namePlaceholder,
  nameRequired,
  descriptionPlaceholder,
  coverUploadType,
  localization,
  excludedLanguages,
  defaults
) {
  const vm = this

  vm.isEdit = isEdit
  vm.viewOnly = !!viewOnly
  vm.modalMode = vm.viewOnly ? 'view' : (vm.isEdit ? 'edit' : 'add')
  vm.config = config
  vm.localization = localization
  vm.nameKey = (config && config.nameKey) || 'name'
  vm.nameLabel = nameLabel || 'Title'
  vm.namePlaceholder = namePlaceholder || vm.nameLabel
  vm.nameRequired = nameRequired !== false
  // Snapshot at init (trimmed). Language-only must not enable Add Save; Edit Update must
  // enable when Language (and other editable fields) change (MW-13603).
  const originalTitle = String(vm.localization[vm.nameKey] || '').trim()
  const originalDescription = String(vm.localization.description || '').trim()
  const originalLanguage = String(vm.localization.language || '').trim()
  let originalDirectors
  let originalStars
  if (!config.hideDirectorsStars) {
    originalDirectors = String(vm.localization.directors || '').trim()
    originalStars = String(vm.localization.stars || '').trim()
  }
  let originalVertical
  let originalHorizontal
  let originalCover
  if (config.verticalKey) {
    originalVertical = String(vm.localization[config.verticalKey] || '').trim()
  }
  if (config.horizontalKey) {
    originalHorizontal = String(vm.localization[config.horizontalKey] || '').trim()
  }
  if (config.coverKey) {
    originalCover = String(vm.localization[config.coverKey] || '').trim()
  }
  vm.descriptionPlaceholder = descriptionPlaceholder || 'Description'
  vm.languages = []
  vm.loading = true
  vm.uploadingVertical = false
  vm.uploadingHorizontal = false
  vm.uploadingCover = false
  vm.imageError = ''

  vm.hintText = (config && config.hintText) || DEFAULT_HINT

  vm.descriptionRequired = !vm.viewOnly && !!(defaults.description && String(defaults.description).trim())
  vm.directorsRequired = !vm.viewOnly && !!(defaults.directors && String(defaults.directors).trim())
  vm.starsRequired = !vm.viewOnly && !!(defaults.stars && String(defaults.stars).trim())
  // Cover is required on Add/Edit Language only when this type has a cover slot
  // (config.coverKey, e.g. Content Set) AND the entity already has a default cover.
  vm.coverRequired = !vm.viewOnly && !!config.coverKey &&
    !!(defaults.images && defaults.images.cover && String(defaults.images.cover).trim())
  // Partner Product Image (verticalImageUrl) is required on Add/Edit Language
  // only for partner_app_vertical uploads when the entity already has a default
  // product image. VOD/TV also have verticalKey, so do not gate on that alone.
  vm.productImageRequired = !vm.viewOnly && config.verticalUploadType === 'partner_app_vertical' &&
    !!(defaults.images && defaults.images.vertical && String(defaults.images.vertical).trim())
  // Partner Product Image (coverImageUrl / horizontal slot) is required on Add/Edit Language
  // only for partner_app_horizontal uploads when the entity already has a default
  // horizontal product image. VOD/TV also have horizontalKey, so do not gate on that alone.
  vm.horizontalProductImageRequired = !vm.viewOnly && config.horizontalUploadType === 'partner_app_horizontal' &&
    !!(defaults.images && defaults.images.horizontal && String(defaults.images.horizontal).trim())
  // Load and sort language list: popular first, rest alphabetical by name.
  // Source is the global ISO-639-1 code -> name map (api/info/language/codes),
  // NOT the per-application i18n locales endpoint — content localization is a
  // separate concept from translating the Subscriber app UI.
  Localization.getLanguageNames()
    .then((codeToName) => {
      const all = Object.keys(codeToName || {})
        .map((tag) => ({ tag, languageName: codeToName[tag] || tag }))
      const excluded = new Set(excludedLanguages || [])
      const filtered = all.filter((l) => !excluded.has(l.tag))

      const popularSet = new Set(POPULAR_LOCALES)
      const popular = POPULAR_LOCALES
        .map((tag) => filtered.find((l) => l.tag === tag))
        .filter(Boolean)
      const rest = filtered
        .filter((l) => !popularSet.has(l.tag))
        .sort((a, b) => (a.languageName || '').localeCompare(b.languageName || ''))

      vm.languages = popular.concat(rest)

      // When viewing or editing, ensure the currently-selected language is
      // present so the dropdown can show its human-readable name.
      if ((vm.isEdit || vm.viewOnly) && vm.localization.language &&
          !vm.languages.some((l) => l.tag === vm.localization.language)) {
        vm.languages.unshift({ tag: vm.localization.language, languageName: vm.localization.language })
      }
    })
    .catch(() => { vm.languages = [] })
    .finally(() => { vm.loading = false })

  function resolveUploadUrl (payload) {
    if (typeof payload === 'string' && payload) {
      return payload
    }
    if (payload && typeof payload === 'object' && typeof payload.resourceUrl === 'string') {
      return payload.resourceUrl
    }
  }

  function uploadFile (file, type) {
    const url = (typeof config.uploadUrlFn === 'function') ? config.uploadUrlFn(entity) : null
    return entity._uploadFile(url, file, { type }).data()
  }

  function makeAspectUploadHandler (axis) {
    const targetKey = config[axis + 'Key']
    const uploadType = config[axis + 'UploadType']
    const aspectMsg = axis === 'vertical'
      ? 'Vertical poster aspect ratio must be 2:3'
      : 'Horizontal poster aspect ratio must be 16:9'
    const divW = axis === 'vertical' ? 2 : 16
    const divH = axis === 'vertical' ? 3 : 9
    const flagKey = axis === 'vertical' ? 'uploadingVertical' : 'uploadingHorizontal'

    return function (file) {
      if (vm.viewOnly || !targetKey) return
      const _URL = window.URL || window.webkitURL
      const img = new window.Image()
      const objectUrl = _URL.createObjectURL(file)
      img.onload = function () {
        const w = this.width / divW
        const h = this.height / divH
        _URL.revokeObjectURL(objectUrl)

        if (file.size > 10485760) {
          alertLiteSvc.alertError('File bigger than 10 MB')
          $scope.$broadcast('dndfile.clear', true)
          return
        }
        if (w / h < 0.98 || w / h > 1.02) {
          alertLiteSvc.alertError(aspectMsg)
          $scope.$broadcast('dndfile.clear', true)
          return
        }

        vm[flagKey] = true
        uploadFile(file, uploadType)
          .then((payload) => {
            const url = resolveUploadUrl(payload)
            if (typeof url === 'string' && url) {
              vm.localization[targetKey] = url
              vm.imageError = ''
            }
          })
          .finally(() => {
            vm[flagKey] = false
            $scope.$broadcast('dndfile.clear', true)
          })
      }
      img.src = objectUrl
    }
  }

  vm.uploadVertical = makeAspectUploadHandler('vertical')
  vm.uploadHorizontal = makeAspectUploadHandler('horizontal')

  vm.uploadCover = function (file) {
    if (vm.viewOnly || !config.coverKey) return
    const _URL = window.URL || window.webkitURL
    const img = new window.Image()
    const objectUrl = _URL.createObjectURL(file)
    img.onload = function () {
      _URL.revokeObjectURL(objectUrl)
      if (file.size > 10485760) {
        alertLiteSvc.alertError('File bigger than 10 MB')
        $scope.$broadcast('dndfile.clear', true)
        return
      }
      if (this.width * this.height > 16800000) {
        alertLiteSvc.alertError('Max resolution of image is 16.8MPx')
        $scope.$broadcast('dndfile.clear', true)
        return
      }
      const type = coverUploadType || config.coverUploadType
      if (!type) {
        $scope.$broadcast('dndfile.clear', true)
        return
      }
      vm.uploadingCover = true
      uploadFile(file, type)
        .then((payload) => {
          const url = resolveUploadUrl(payload)
          if (typeof url === 'string' && url) {
            vm.localization[config.coverKey] = url
            vm.imageError = ''
          }
        })
        .finally(() => {
          vm.uploadingCover = false
          $scope.$broadcast('dndfile.clear', true)
        })
    }
    img.src = objectUrl
  }

  vm.clearVertical = function () {
    if (vm.viewOnly || !config.verticalKey) return
    vm.localization[config.verticalKey] = ''
  }

  vm.clearHorizontal = function () {
    if (vm.viewOnly || !config.horizontalKey) return
    vm.localization[config.horizontalKey] = ''
  }

  vm.clearCover = function () {
    if (vm.viewOnly || !config.coverKey) return
    vm.localization[config.coverKey] = ''
  }

  vm.cancel = function () {
    $uibModalInstance.dismiss('cancel')
  }

  vm.isSaveDisabled = function (form) {
    if (vm.uploadingVertical || vm.uploadingHorizontal || vm.uploadingCover) {
      return true
    }
    const currentTitle = String(vm.localization[vm.nameKey] || '').trim()
    if (vm.nameRequired && !currentTitle) {
      return true
    }
    const currentDescription = String(vm.localization.description || '').trim()
    // Ignore form.$dirty/$pristine: input-with-counter writeback marks the form
    // dirty without a real name/description change. Language-only must not enable Add Save;
    // Edit Update must enable when Language (and other editable fields) change (MW-13603).
    if (currentTitle !== originalTitle || currentDescription !== originalDescription) {
      return false
    }
    if (vm.isEdit) {
      const currentLanguage = String(vm.localization.language || '').trim()
      if (currentLanguage !== originalLanguage) {
        return false
      }
      if (originalDirectors !== undefined) {
        const currentDirectors = String(vm.localization.directors || '').trim()
        if (currentDirectors !== originalDirectors) {
          return false
        }
      }
      if (originalStars !== undefined) {
        const currentStars = String(vm.localization.stars || '').trim()
        if (currentStars !== originalStars) {
          return false
        }
      }
      if (originalVertical !== undefined) {
        const currentVertical = String(vm.localization[config.verticalKey] || '').trim()
        if (currentVertical !== originalVertical) {
          return false
        }
      }
      if (originalHorizontal !== undefined) {
        const currentHorizontal = String(vm.localization[config.horizontalKey] || '').trim()
        if (currentHorizontal !== originalHorizontal) {
          return false
        }
      }
      if (originalCover !== undefined) {
        const currentCover = String(vm.localization[config.coverKey] || '').trim()
        if (currentCover !== originalCover) {
          return false
        }
      }
    }
    return true
  }

  vm.save = function (form) {
    if (vm.viewOnly) {
      vm.cancel()
      return
    }
    if (form && typeof form.$setSubmitted === 'function') {
      form.$setSubmitted()
    }
    // Force every registered control to run its validators so required-field
    // errors surface on an empty/pristine form (mirrors addSeason.controller).
    if (form && form.$$controls) {
      for (const control of form.$$controls) {
        control.$setDirty()
        control.$validate()
      }
    }
    if (form && form.$invalid) return
    if (vm.nameRequired && !vm.localization.language) return
    if (vm.coverRequired) {
      const cover = vm.localization[config.coverKey]
      if (!cover || !String(cover).trim()) {
        vm.imageError = 'Localized Cover image is required'
        return
      }
    }
    if (vm.productImageRequired) {
      const productImage = vm.localization[config.verticalKey]
      if (!productImage || !String(productImage).trim()) {
        vm.imageError = 'Localized Product Image is required'
        return
      }
    }
    if (vm.horizontalProductImageRequired) {
      const horizontalProductImage = vm.localization[config.horizontalKey]
      if (!horizontalProductImage || !String(horizontalProductImage).trim()) {
        vm.imageError = 'Localized Product Image is required'
        return
      }
    }
    $uibModalInstance.close(vm.localization)
  }
}
